import React, { createContext, useState, useEffect, useContext, useMemo, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import ThemeToggle from './components/ThemeToggle';
import { Menu, X } from 'lucide-react';

// Pages & Components
import MarketingHome from './pages/MarketingHome';
import TradiesLanding from './pages/TradiesLanding';
import JobsRouter from './components/JobsRouter';
import ProtectedJobsRoute from './components/ProtectedJobsRoute';
import TradiesOnboarding from './pages/TradiesOnboarding';
import JobsOnboarding from './pages/JobsOnboarding';
import JobsDashboard from './pages/JobsDashboard';
import JobsBrowse from './pages/JobsBrowse';
import JobDetail from './pages/JobDetail';
import JobApply from './pages/JobApply';
import JobsLoginPage from './pages/JobsLoginPage';
import JobsRegisterPage from './pages/JobsRegisterPage';

import TalentLanding from './pages/TalentLanding';
import TalentOverview from './pages/TalentOverview';
import TalentProfileEdit from './pages/TalentProfileEdit';
import TalentMediaManager from './pages/TalentMediaManager';
import TalentSubscribe from './pages/TalentSubscribe';
import EmployerTalentSearch from './pages/EmployerTalentSearch';
import CandidateDetail from './pages/CandidateDetail';
import AccessPassPurchase from './pages/AccessPassPurchase';
import OfferCreate from './pages/OfferCreate';
import InterviewInviteCreate from './pages/InterviewInviteCreate';
import TalentOffers from './pages/TalentOffers';
import TalentInterviews from './pages/TalentInterviews';
import TalentMessaging from './pages/TalentMessaging';
import JobWizard from './pages/JobWizard';
import PortalLayout from './components/PortalLayout';

import BrowsePage from './pages/BrowsePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import TradieProfilePage from './pages/TradieProfilePage';
import MessagesPage from './pages/MessagesPage';
import TradieDashboard from './pages/TradieDashboard';

import AdminDashboard from './pages/AdminDashboard';
import AdminActivityLogs from './pages/AdminActivityLogs';
import AdminUsers from './pages/AdminUsers';
import AdminFeatureFlags from './pages/AdminFeatureFlags';
import AdminPricing from './pages/AdminPricing';
import AdminExport from './pages/AdminExport';
import AdminVerification from './pages/AdminVerification';
import AdminServices from './pages/AdminServices';
import AdminManageAdmins from './pages/AdminManageAdmins';
import AdminTradies from './pages/AdminTradies';
import AdminQuotes from './pages/AdminQuotes';
import AdminReports from './pages/AdminReports';
import AdminJobListings from './pages/AdminJobListings';
import AdminApplications from './pages/AdminApplications';
import AdminCompanies from './pages/AdminCompanies';
import AdminGoJobs from './pages/AdminGoJobs';
import AdminGoTalent from './pages/AdminGoTalent';

import AboutTradies from './pages/AboutTradies';
import AboutJobs from './pages/AboutJobs';
import AboutTalent from './pages/AboutTalent';
import PricingTradies from './pages/PricingTradies';
import PricingJobs from './pages/PricingJobs';
import PricingTalent from './pages/PricingTalent';

import MySavedPage from './pages/MySavedPage';
import PortalSavedPage from './pages/PortalSavedPage';
import ForcePasswordChange from './pages/ForcePasswordChange';
import GoFeedHome from './pages/GoFeedHome';
import ProductFeed from './pages/ProductFeed';
import GoNews from './pages/GoNews';
import HomeFeedNews from './pages/HomeFeedNews';
import SettingsPage from './pages/SettingsPage';
import UserProfilePage from './pages/UserProfilePage';
import PostDetailPage from './pages/PostDetailPage';
import NewsDetailPage from './pages/NewsDetailPage';
import MyPostsPage from './pages/MyPostsPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import SavedTradiesPage from './pages/SavedTradiesPage';
import PostJobPage from './pages/PostJobPage';
import ProfilePage from './pages/ProfilePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import NotificationBell from './components/NotificationBell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// =========================
// Auth Context
// =========================
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'system');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('viewMode') || 'grid');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }

    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('viewMode', viewMode);
  }, [viewMode]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem('token', response.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      await fetchUser();
      return {
        success: true,
        data: response.data,
        must_change_password: response.data.must_change_password,
      };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (data) => {
    try {
      const response = await axios.post(`${API}/auth/register`, data);
      localStorage.setItem('token', response.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      await fetchUser();
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const updatePreferences = async (newTheme, newViewMode) => {
    if (newTheme !== undefined) setTheme(newTheme);
    if (newViewMode !== undefined) setViewMode(newViewMode);

    if (user) {
      try {
        await axios.patch(`${API}/auth/preferences`, {
          theme_pref: newTheme,
          view_pref: newViewMode,
        });
      } catch (error) {
        console.error('Failed to update preferences:', error);
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        theme,
        setTheme: (t) => updatePreferences(t, undefined),
        viewMode,
        setViewMode: (v) => updatePreferences(undefined, v),
        fetchUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// =========================
// Helpers
// =========================
const ScrollToTop = () => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, location.search]);
  return null;
};

const ProtectedRoute = ({ children, requireRole }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (requireRole && user.role !== requireRole) return <Navigate to="/" replace />;

  return children;
};

// =========================
// Logo Component
// =========================
const BrandLogo = ({ className = 'h-8' }) => {
  return (
    <span className="inline-flex items-center">
      <img
        src="/assets/slogocolor.png"
        alt="GoFella"
        className={`${className} dark:hidden`}
        draggable="false"
      />
      <img
        src="/assets/slogowhite.png"
        alt="GoFella"
        className={`${className} hidden dark:block`}
        draggable="false"
      />
    </span>
  );
};

// =========================
// Layouts
// =========================
const SimpleLayout = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="inline-flex items-center" aria-label="GoFella Home">
            <BrandLogo className="h-7 sm:h-8" />
          </button>

          <div className="flex items-center gap-4">
            <ThemeToggle />
            {user ? (
              <button
                onClick={() =>
                  navigate(
                    user.has_tradies_profile
                      ? '/tradies/dashboard'
                      : user.has_jobs_profile
                      ? '/jobs/dashboard'
                      : '/tradies'
                  )
                }
                className="px-4 py-2 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Dashboard
              </button>
            ) : (
              <>
                <button onClick={() => navigate('/login')} className="px-4 py-2 hover:bg-muted rounded-xl text-foreground">
                  Login
                </button>
                <button
                  onClick={() => navigate('/register')}
                  className="px-4 py-2 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  Sign Up
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t py-8 bg-muted/30">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© 2025 GoFella. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

const MainLayout = ({ children, productType = 'tradies' }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  const moreRef = useRef(null);
  const accountRef = useRef(null);

  useEffect(() => {
    setMoreOpen(false);
    setAccountOpen(false);
    setMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handler = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
      if (accountRef.current && !accountRef.current.contains(e.target)) setAccountOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const getNavLinks = (pt, u) => {
    const role = u?.role;
    const isLoggedIn = !!u;

    if (pt === 'tradies') {
      if (role === 'tradie') {
        return [
          { label: 'Home', path: '/tradies' },
          { label: 'Browse', path: '/tradies/browse' },
          { label: 'Dashboard', path: '/tradies/dashboard' },
          { label: 'Messages', path: '/messages' },
          { label: 'About', path: '/abouttradies' },
          { label: 'Pricing', path: '/pricingtradies' },
        ];
      }

      if (isLoggedIn) {
        return [
          { label: 'Home', path: '/tradies' },
          { label: 'Browse', path: '/tradies/browse' },
          ...(role === 'customer' ? [{ label: 'Saved', path: '/saved' }] : []),
          { label: 'About', path: '/abouttradies' },
          { label: 'Pricing', path: '/pricingtradies' },
        ];
      }

      return [
        { label: 'Home', path: '/tradies' },
        { label: 'Browse', path: '/tradies/browse' },
        { label: 'About', path: '/abouttradies' },
        { label: 'Pricing', path: '/pricingtradies' },
      ];
    }

    if (pt === 'jobs') {
      if (role === 'employer') {
        return [
          { label: 'Home', path: '/jobs' },
          { label: 'Browse', path: '/jobs/browse' },
          { label: 'Dashboard', path: '/jobs/dashboard' },
          { label: 'Post Job', path: '/portal/gojobs/new' },
          { label: 'About', path: '/aboutjobs' },
          { label: 'Pricing', path: '/pricingjobs' },
        ];
      }

      return [
        { label: 'Home', path: '/jobs' },
        { label: 'Browse', path: '/jobs/browse' },
        { label: 'About', path: '/aboutjobs' },
        { label: 'Pricing', path: '/pricingjobs' },
      ];
    }

    if (pt === 'talent') {
      if (role === 'employer') {
        return [
          { label: 'Home', path: '/talent' },
          { label: 'Browse', path: '/portal/talent' },
          { label: 'Messages', path: '/portal/talent/messages' },
          { label: 'About', path: '/abouttalent' },
          { label: 'Pricing', path: '/pricingtalent' },
        ];
      }

      if (isLoggedIn) {
        return [
          { label: 'Home', path: '/talent' },
          { label: 'My Profile', path: '/talent/me' },
          { label: 'Edit Profile', path: '/talent/me/profile' },
          { label: 'Media', path: '/talent/me/media' },
          { label: 'Offers', path: '/talent/me/offers' },
          { label: 'Interviews', path: '/talent/me/interviews' },
          { label: 'About', path: '/abouttalent' },
          { label: 'Pricing', path: '/pricingtalent' },
        ];
      }

      return [
        { label: 'Home', path: '/talent' },
        { label: 'About', path: '/abouttalent' },
        { label: 'Pricing', path: '/pricingtalent' },
      ];
    }

    return [
      { label: 'Home', path: '/tradies' },
      { label: 'Browse', path: '/tradies/browse' },
      { label: 'About', path: '/abouttradies' },
      { label: 'Pricing', path: '/pricingtradies' },
    ];
  };

  const navLinks = useMemo(() => getNavLinks(productType, user), [productType, user]);

  const PRIMARY_LABELS = new Set(['Home', 'Browse']);
  const primaryLinks = navLinks.filter((l) => PRIMARY_LABELS.has(l.label));
  const moreLinks = navLinks.filter((l) => !PRIMARY_LABELS.has(l.label));

  const switchToPath = productType === 'tradies' ? '/jobs' : productType === 'jobs' ? '/talent' : '/tradies';
  const switchToLabel = productType === 'tradies' ? 'GoJobs' : productType === 'jobs' ? 'GoTalent' : 'GoTradies';

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            <button onClick={() => navigate('/')} className="inline-flex items-center" aria-label="GoFella Home">
              <BrandLogo className="h-7 sm:h-8" />
            </button>

            <nav className="hidden md:flex items-center gap-6">
              {primaryLinks.map((link) => (
                <button
                  key={link.path}
                  onClick={() => navigate(link.path)}
                  className="hover:text-primary transition-colors text-foreground"
                >
                  {link.label}
                </button>
              ))}

              {moreLinks.length > 0 && (
                <div className="relative" ref={moreRef}>
                  <button onClick={() => setMoreOpen((v) => !v)} className="hover:text-primary transition-colors text-foreground">
                    More ▾
                  </button>

                  {moreOpen && (
                    <div className="absolute left-0 mt-2 w-56 rounded-xl border border-border bg-background shadow-lg overflow-hidden">
                      {moreLinks.map((link) => (
                        <button
                          key={link.path}
                          onClick={() => {
                            setMoreOpen(false);
                            navigate(link.path);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted text-foreground"
                        >
                          {link.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {user && (
                <button
                  onClick={() => navigate(switchToPath)}
                  className="text-sm px-3 py-1.5 border border-border rounded-lg hover:bg-muted text-foreground"
                >
                  Go to {switchToLabel}
                </button>
              )}
            </nav>

            <div className="flex items-center gap-3">
              <ThemeToggle />

              {user ? (
                <>
                  <NotificationBell />

                  <div className="relative hidden md:block" ref={accountRef}>
                    <button
                      onClick={() => setAccountOpen((v) => !v)}
                      className="hover:text-primary text-foreground px-2 py-1 rounded-lg hover:bg-muted"
                    >
                      {user.full_name}
                    </button>

                    {accountOpen && (
                      <div className="absolute right-0 mt-2 w-48 rounded-xl border border-border bg-background shadow-lg overflow-hidden">
                        <button
                          onClick={() => {
                            setAccountOpen(false);
                            navigate('/profile');
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted text-foreground"
                        >
                          Profile
                        </button>

                        {user.role === 'admin' && (
                          <button
                            onClick={() => {
                              setAccountOpen(false);
                              navigate('/admin');
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-muted text-foreground"
                          >
                            Admin
                          </button>
                        )}

                        <button
                          onClick={() => {
                            setAccountOpen(false);
                            logout();
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted text-foreground"
                        >
                          Logout
                        </button>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => setMobileMenuOpen((v) => !v)}
                    className="md:hidden p-2 rounded-lg hover:bg-muted"
                    aria-label="Open menu"
                  >
                    {mobileMenuOpen ? <X /> : <Menu />}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/login')}
                    className="hidden md:block px-4 py-2 hover:bg-muted rounded-xl text-foreground"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => navigate('/register')}
                    className="hidden md:block px-4 py-2 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    Sign Up
                  </button>

                  <button
                    onClick={() => setMobileMenuOpen((v) => !v)}
                    className="md:hidden p-2 rounded-lg hover:bg-muted"
                    aria-label="Open menu"
                  >
                    {mobileMenuOpen ? <X /> : <Menu />}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t bg-background">
            <div className="px-4 py-4 space-y-4">
              <div className="text-xs text-muted-foreground">Menu</div>

              <div className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <button
                    key={link.path}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate(link.path);
                    }}
                    className="text-left px-3 py-2 rounded-lg hover:bg-muted text-foreground"
                  >
                    {link.label}
                  </button>
                ))}
              </div>

              {user ? (
                <div className="pt-3 border-t border-border space-y-2">
                  <div className="text-xs text-muted-foreground">Account</div>

                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate('/profile');
                    }}
                    className="text-left w-full px-3 py-2 rounded-lg hover:bg-muted text-foreground"
                  >
                    Profile
                  </button>

                  {user.role === 'admin' && (
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        navigate('/admin');
                      }}
                      className="text-left w-full px-3 py-2 rounded-lg hover:bg-muted text-foreground"
                    >
                      Admin
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate(switchToPath);
                    }}
                    className="text-left w-full px-3 py-2 rounded-lg hover:bg-muted text-foreground"
                  >
                    Go to {switchToLabel}
                  </button>

                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      logout();
                    }}
                    className="text-left w-full px-3 py-2 rounded-lg hover:bg-muted text-foreground"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <div className="pt-3 border-t border-border flex flex-col gap-2">
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate('/login');
                    }}
                    className="px-3 py-2 rounded-lg hover:bg-muted text-foreground text-left"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate('/register');
                    }}
                    className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-left"
                  >
                    Sign Up
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t py-8 bg-muted/30">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© 2025 GoFella. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

// =========================
// AppRoutes (fix: can access user)
// =========================
const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Home - Feed/News */}
        <Route path="/" element={<HomeFeedNews />} />
        <Route path="/about" element={<SimpleLayout><MarketingHome /></SimpleLayout>} />
        <Route path="/feed" element={<GoFeedHome />} />
        <Route path="/news" element={<GoNews />} />

        {/* Auth Routes */}
        <Route path="/login" element={<SimpleLayout><LoginPage /></SimpleLayout>} />
        <Route path="/register" element={<SimpleLayout><RegisterPage /></SimpleLayout>} />
        <Route path="/forgot-password" element={<SimpleLayout><ForgotPasswordPage /></SimpleLayout>} />
        <Route path="/reset-password" element={<SimpleLayout><ResetPasswordPage /></SimpleLayout>} />
        <Route path="/change-password" element={<ForcePasswordChange />} />

        {/* GoTradies Routes */}
        <Route path="/tradies" element={<MainLayout productType="tradies"><TradiesLanding /></MainLayout>} />
        <Route path="/tradies/browse" element={<MainLayout productType="tradies"><BrowsePage /></MainLayout>} />
        <Route path="/tradies/news" element={<MainLayout productType="tradies"><ProductFeed productContext="TRADIES" title="GoTradies News & Updates" /></MainLayout>} />
        <Route path="/tradies/services/:serviceId" element={<MainLayout productType="tradies"><ServiceDetailPage /></MainLayout>} />
        <Route path="/tradies/tradie/:tradieId" element={<MainLayout productType="tradies"><TradieProfilePage /></MainLayout>} />
        <Route path="/tradies/onboarding" element={<MainLayout productType="tradies"><TradiesOnboarding /></MainLayout>} />
        <Route path="/tradies/dashboard" element={<MainLayout productType="tradies"><ProtectedRoute requireRole="tradie"><TradieDashboard /></ProtectedRoute></MainLayout>} />

        {/* Backward compatibility */}
        <Route path="/browse" element={<Navigate to="/tradies/browse" replace />} />
        <Route path="/dashboard" element={<Navigate to="/tradies/dashboard" replace />} />

        <Route path="/abouttradies" element={<MainLayout productType="tradies"><AboutTradies /></MainLayout>} />
        <Route path="/aboutjobs" element={<MainLayout productType="jobs"><AboutJobs /></MainLayout>} />
        <Route path="/abouttalent" element={<MainLayout productType="talent"><AboutTalent /></MainLayout>} />

        <Route path="/pricingtradies" element={<MainLayout productType="tradies"><PricingTradies /></MainLayout>} />
        <Route path="/pricingjobs" element={<MainLayout productType="jobs"><PricingJobs /></MainLayout>} />
        <Route path="/pricingtalent" element={<MainLayout productType="talent"><PricingTalent /></MainLayout>} />

        <Route path="/messages" element={<MainLayout productType="tradies"><ProtectedRoute><MessagesPage /></ProtectedRoute></MainLayout>} />
        <Route path="/saved" element={<MainLayout productType="tradies"><ProtectedRoute><SavedTradiesPage /></ProtectedRoute></MainLayout>} />
        <Route path="/post-job" element={<MainLayout productType="tradies"><ProtectedRoute><PostJobPage /></ProtectedRoute></MainLayout>} />
        <Route path="/subscription-success" element={<MainLayout productType="tradies"><ProtectedRoute><SubscriptionSuccessPage /></ProtectedRoute></MainLayout>} />

        {/* GoJobs Routes */}
        <Route path="/jobs" element={<MainLayout productType="jobs"><JobsRouter /></MainLayout>} />
        <Route path="/jobs/login" element={<JobsLoginPage />} />
        <Route path="/jobs/register" element={<JobsRegisterPage />} />
        <Route path="/jobs/browse" element={<MainLayout productType="jobs"><ProtectedJobsRoute><JobsBrowse /></ProtectedJobsRoute></MainLayout>} />
        <Route path="/jobs/news" element={<MainLayout productType="jobs"><ProtectedJobsRoute><ProductFeed productContext="JOBS" title="GoJobs News & Opportunities" /></ProtectedJobsRoute></MainLayout>} />
        <Route path="/jobs/:jobId" element={<MainLayout productType="jobs"><ProtectedJobsRoute><JobDetail /></ProtectedJobsRoute></MainLayout>} />
        <Route path="/jobs/:jobId/apply" element={<MainLayout productType="jobs"><ProtectedRoute><ProtectedJobsRoute><JobApply /></ProtectedJobsRoute></ProtectedRoute></MainLayout>} />
        <Route path="/jobs/onboarding" element={<MainLayout productType="jobs"><ProtectedJobsRoute><JobsOnboarding /></ProtectedJobsRoute></MainLayout>} />
        <Route path="/jobs/dashboard" element={<MainLayout productType="jobs"><ProtectedRoute><ProtectedJobsRoute><JobsDashboard /></ProtectedJobsRoute></ProtectedRoute></MainLayout>} />

        {/* GoTalent Routes */}
        <Route path="/talent" element={<MainLayout productType="talent"><TalentLanding /></MainLayout>} />
        <Route path="/talent/news" element={<MainLayout productType="talent"><ProductFeed productContext="TALENT" title="GoTalent News & Insights" /></MainLayout>} />
        <Route path="/talent/me" element={<MainLayout productType="talent"><ProtectedRoute><TalentOverview /></ProtectedRoute></MainLayout>} />
        <Route path="/talent/me/profile" element={<MainLayout productType="talent"><ProtectedRoute><TalentProfileEdit /></ProtectedRoute></MainLayout>} />
        <Route path="/talent/me/media" element={<MainLayout productType="talent"><ProtectedRoute><TalentMediaManager /></ProtectedRoute></MainLayout>} />
        <Route path="/talent/me/offers" element={<MainLayout productType="talent"><ProtectedRoute><TalentOffers /></ProtectedRoute></MainLayout>} />
        <Route path="/talent/me/interviews" element={<MainLayout productType="talent"><ProtectedRoute><TalentInterviews /></ProtectedRoute></MainLayout>} />
        <Route path="/talent/subscribe" element={<TalentSubscribe />} />

        {/* Portal Routes */}
        <Route path="/portal" element={<ProtectedRoute><PortalLayout><div className="text-center py-20"><h1 className="text-3xl font-bold">Client Portal</h1><p className="text-muted-foreground">Manage your listings and applications</p></div></PortalLayout></ProtectedRoute>} />
        <Route path="/portal/gojobs/new" element={<ProtectedRoute><ProtectedJobsRoute><JobWizard /></ProtectedJobsRoute></ProtectedRoute>} />
        <Route path="/portal/talent" element={<ProtectedRoute><EmployerTalentSearch /></ProtectedRoute>} />
        <Route path="/portal/talent/:candidateId" element={<ProtectedRoute><CandidateDetail /></ProtectedRoute>} />
        <Route path="/portal/talent/access" element={<ProtectedRoute><AccessPassPurchase /></ProtectedRoute>} />
        <Route path="/portal/talent/offers/new" element={<ProtectedRoute><OfferCreate /></ProtectedRoute>} />
        <Route path="/portal/talent/interviews/new" element={<ProtectedRoute><InterviewInviteCreate /></ProtectedRoute>} />
        <Route path="/portal/talent/messages" element={<ProtectedRoute><TalentMessaging /></ProtectedRoute>} />
        <Route path="/portal/saved" element={<ProtectedRoute><PortalSavedPage /></ProtectedRoute>} />

        {/* Shared */}
        <Route path="/profile" element={<MainLayout><ProtectedRoute><ProfilePage /></ProtectedRoute></MainLayout>} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/users/:userId" element={<UserProfilePage />} />
        <Route path="/posts/:postId" element={<PostDetailPage />} />
        <Route path="/news/:newsId" element={<NewsDetailPage />} />
        <Route path="/me/posts" element={<ProtectedRoute><MyPostsPage /></ProtectedRoute>} />
        <Route path="/portal/posts" element={<ProtectedRoute><MyPostsPage /></ProtectedRoute>} />
        <Route path="/me/saved" element={<MainLayout><ProtectedRoute><MySavedPage /></ProtectedRoute></MainLayout>} />

        {/* Admin */}
        <Route path="/admin" element={<ProtectedRoute requireRole="admin"><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute requireRole="admin"><AdminUsers /></ProtectedRoute>} />
        <Route path="/admin/tradies" element={<ProtectedRoute requireRole="admin"><AdminTradies /></ProtectedRoute>} />
        <Route path="/admin/verification" element={<ProtectedRoute requireRole="admin"><AdminVerification /></ProtectedRoute>} />
        <Route path="/admin/services" element={<ProtectedRoute requireRole="admin"><AdminServices /></ProtectedRoute>} />
        <Route path="/admin/quotes" element={<ProtectedRoute requireRole="admin"><AdminQuotes /></ProtectedRoute>} />
        <Route path="/admin/reports" element={<ProtectedRoute requireRole="admin"><AdminReports /></ProtectedRoute>} />
        <Route path="/admin/companies" element={<ProtectedRoute requireRole="admin"><AdminCompanies /></ProtectedRoute>} />
        <Route path="/admin/gojobs" element={<ProtectedRoute requireRole="admin"><AdminGoJobs /></ProtectedRoute>} />
        <Route path="/admin/job-listings" element={<ProtectedRoute requireRole="admin"><AdminJobListings /></ProtectedRoute>} />
        <Route path="/admin/applications" element={<ProtectedRoute requireRole="admin"><AdminApplications /></ProtectedRoute>} />
        <Route path="/admin/talent" element={<ProtectedRoute requireRole="admin"><AdminGoTalent /></ProtectedRoute>} />
        <Route path="/admin/logs" element={<ProtectedRoute requireRole="admin"><AdminActivityLogs /></ProtectedRoute>} />
        <Route path="/admin/feature-flags" element={<ProtectedRoute requireRole="admin"><AdminFeatureFlags /></ProtectedRoute>} />
        <Route path="/admin/pricing" element={<ProtectedRoute requireRole="admin"><AdminPricing /></ProtectedRoute>} />
        <Route path="/admin/export" element={<ProtectedRoute requireRole="admin"><AdminExport /></ProtectedRoute>} />
        <Route path="/admin/manage-admins" element={<ProtectedRoute requireRole="admin"><AdminManageAdmins /></ProtectedRoute>} />

        {/* 404 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <Toaster position="top-right" expand={false} richColors />
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
